import * as React from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Separator } from '../../ui/separator';
import { DrawerSectionTitle } from './drawer-section-title';

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * Shared Tailwind classes for field labels inside a DrawerSection.
 * Exported so that composites that host multiple titled sub-blocks inside one
 * DrawerSection card (e.g. PickupDeliverySection) can reuse the exact same
 * label style without duplicating the class string.
 */
export const DRAWER_FIELD_LABEL_CLASSES = 'text-sm font-medium text-foreground/60 tracking-wide mb-1.5' as const;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DrawerSectionCell {
  /** The cell value content */
  cell: React.ReactNode;
  /** The label shown above the cell value */
  label: React.ReactNode;
}

export interface DrawerSectionRow {
  /** One, two, or three cells for this row — single cell spans full width */
  cells:
    | [DrawerSectionCell]
    | [DrawerSectionCell, DrawerSectionCell]
    | [DrawerSectionCell, DrawerSectionCell, DrawerSectionCell];
  /**
   * Optional column-count override for the row grid — only honored for
   * 2-cell rows. Use `3` to align a 2-cell row underneath a 3-column row
   * above it (e.g. Tags/Value below Handling units/Weight/Volume), leaving
   * the third track empty. Ignored for 1-cell and 3-cell rows, which always
   * render at their natural cell count.
   */
  columns?: 2 | 3;
}

export interface DrawerSectionProps {
  /**
   * Full-width slot rendered under the section header and above the body.
   * Use for error banners or other full-width notifications — e.g. an ErrorBanner.
   */
  banner?: React.ReactNode;
  /** Optional extra CSS classes on the outer card */
  className?: string;
  /**
   * Free-form body content rendered below the rows.
   * Used when the rows API is not sufficient (e.g. nested sub-blocks).
   */
  children?: React.ReactNode;
  /** Optional slot rendered to the right of the section header */
  headerRight?: React.ReactNode;
  /** Lucide icon rendered inside the primary-tinted badge */
  icon: LucideIcon;
  /** Optional separator between rows and children */
  separateChildren?: boolean;
  /** Optional inline suffix rendered right after the title */
  titleSuffix?: React.ReactNode;
  /** Typed two-column row data */
  rows?: DrawerSectionRow[];
  /** Section title */
  title: React.ReactNode;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * Re-export FormSectionHeader as the canonical sub-block header for DrawerSection children.
 * Use this when composing multiple titled sub-blocks inside a single DrawerSection card.
 */
export { FormSectionHeader as DrawerSectionHeader } from '../form-section-header';
export type { FormSectionHeaderProps as DrawerSectionHeaderProps } from '../form-section-header';

const ROW_GRID_COLS_CLASSES: Record<1 | 2 | 3, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
};

/**
 * Resolves the row's grid column count. The `columns` override only applies to 2-cell rows
 * (to align them under a 3-column row above); 1-cell and 3-cell rows always use their cell count.
 */
function resolveRowGridColsClass(row: DrawerSectionRow): string {
  const columns = row.cells.length === 2 ? (row.columns ?? row.cells.length) : row.cells.length;
  return ROW_GRID_COLS_CLASSES[columns as 1 | 2 | 3];
}

interface DrawerSectionRowsProps {
  readonly rows: DrawerSectionRow[];
}

function DrawerSectionRows({ rows }: DrawerSectionRowsProps): React.JSX.Element {
  return (
    <div className="space-y-4">
      {rows.map((row, rowIndex) => (
        <div className={cn('grid gap-x-4 gap-y-4 text-sm', resolveRowGridColsClass(row))} key={rowIndex}>
          {row.cells.map((cell, cellIndex) => (
            <div key={cellIndex}>
              <p className={DRAWER_FIELD_LABEL_CLASSES}>{cell.label}</p>
              <div>{cell.cell}</div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

interface DrawerSectionBodyProps {
  readonly children?: React.ReactNode;
  readonly hasRows: boolean;
  readonly rows?: DrawerSectionRow[];
  readonly separateChildren: boolean;
}

function DrawerSectionBody({ children, hasRows, rows, separateChildren }: DrawerSectionBodyProps): React.JSX.Element {
  return (
    <div className="px-4 pb-4">
      {hasRows && rows !== undefined && <DrawerSectionRows rows={rows} />}
      {children !== undefined && (
        <>
          {separateChildren && hasRows && <Separator className="my-4" />}
          {children}
        </>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Rounded card section for drawer detail blocks.
 * Renders a primary-tinted icon badge + title header, followed by a typed
 * two-column rows grid. Falls back to `children` for free-form content.
 *
 * For composites that need multiple titled sub-blocks inside one card
 * (e.g. pickup + delivery), use `children` mode: provide `DrawerSectionHeader`
 * (re-exported FormSectionHeader) and `DRAWER_FIELD_LABEL_CLASSES` for the secondary sub-block.
 */
export function DrawerSection({
  banner,
  children,
  className,
  headerRight,
  icon: Icon,
  rows,
  separateChildren = false,
  title,
  titleSuffix,
}: DrawerSectionProps): React.JSX.Element {
  const hasRows = rows !== undefined && rows.length > 0;
  const hasBody = hasRows || children !== undefined;

  return (
    <div className={cn('rounded-xl border bg-card shadow-sm', className)}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-2">
        <DrawerSectionTitle icon={Icon} title={title} titleSuffix={titleSuffix} />
        {headerRight !== undefined && <div className="shrink-0">{headerRight}</div>}
      </div>

      {/* Banner — inset slot under the header, aligned with card padding */}
      {banner !== undefined && <div className="px-4 pb-2">{banner}</div>}

      {/* Body */}
      {hasBody && (
        <DrawerSectionBody hasRows={hasRows} rows={rows} separateChildren={separateChildren}>
          {children}
        </DrawerSectionBody>
      )}
    </div>
  );
}
